import { Injectable } from '@angular/core';
import { BatchItem, BatchItemUpdate } from '@buyiq-app/batch/models/batch-item';
import {
    ProductParameters,
    ProductSearchType,
} from '@buyiq-app/product/models/product';
import {
    createScanKeyFromConfig,
    ProductScanConfiguration,
    scanSourceNotificationSuffix,
    ScheduledScan,
    ScanKey,
} from '@buyiq-core/models/scan';
import { UserService } from '@buyiq-core/user/user.service';
import {
    Notification,
    NotificationType,
} from '@buyiq-shared/models/notification';
import { combineLatest, concatMap, Observable, of, ReplaySubject } from 'rxjs';
import { filter, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { ScanPipelineService } from './scan-pipeline.service';
import { ScanQueueService } from './scan-queue.service';
import { ProductNotificationId } from '@buyiq-app/product/models/product-notification';

@Injectable({
    providedIn: 'root',
})
export class ScanService {
    private scheduledScan: ScheduledScan;
    private inProgressScans = new Set<ScanKey>();

    constructor(
        private userService: UserService,
        private scanPipelineService: ScanPipelineService,
        private scanQueueService: ScanQueueService
    ) {}

    getScheduledScan(): ScheduledScan {
        const scan = this.scheduledScan;
        this.scheduledScan = null;
        return scan;
    }

    scheduleScan(scheduledScan: ScheduledScan): void {
        this.scheduledScan = scheduledScan;
    }

    buildScheduledScanNotification(scheduledScan: ScheduledScan): Notification {
        const destination =
            scanSourceNotificationSuffix.get(scheduledScan.source) ??
            'Previous Page';
        let destinationId: number;
        if (destination === 'Search') {
            destinationId = ProductNotificationId.ReturnToSearch;
        } else if (destination === 'My Favorite Items List') {
            destinationId = ProductNotificationId.ReturnToFavorites;
        } else {
            destinationId = 100;
        }
        return new Notification({
            type: NotificationType.Info,
            text: `Return to ${destination}`,
            id: destinationId,
        });
    }

    /**
     * @description Scans a product and updates it in the batch. This method does not set the product as the active product in the batch
     * service. Individual components should set that value. This allows scans to happen in the background without affecting the currently
     * displayed item.
     */
    scanProduct(config: ProductScanConfiguration): Observable<BatchItemUpdate> {
        // On the first scan, we generate an API request. Subsequent scans return null to avoid multiple requests.
        // The product page will filter out null requests, so only the first scan's API request will result in a UI update.
        let apiRequest = of(null) as Observable<BatchItemUpdate>;
        const scanKey = createScanKeyFromConfig(config);

        if (!this.inProgressScans.has(scanKey)) {
            this.inProgressScans.add(scanKey);

            const queueBuffer = new ReplaySubject<Array<ScanKey>>(1);
            const queueBufferSubscription = this.getMatchingScanBuffer(
                scanKey
            ).subscribe(queueBuffer);

            this.scanQueueService.queueScan(scanKey);

            const batchItemRequest = this.userService.getCurrentUser().pipe(
                tap((currentUser) => (config.user = currentUser)),
                map(() =>
                    this.scanPipelineService.getBatchItem(
                        config.searchText,
                        config?.productParameters?.searchType ??
                            ProductSearchType.Upc
                    )
                ),
                concatMap((batchItemUpdate) =>
                    this.scanPipelineService.getBatchItemProductData(
                        batchItemUpdate,
                        config
                    )
                )
            );
            apiRequest = combineLatest({
                batchItemUpdate: batchItemRequest,
                // This will be triggered when the user scans a different value or stops scanning for the debounce period
                queueBuffer: queueBuffer.asObservable(),
            }).pipe(
                map((streams) => {
                    const batchItemUpdate = streams.batchItemUpdate;
                    const aplDiscontinued =
                        batchItemUpdate?.product?.aplDiscontinued;
                    config.numberOfTimesScanned =
                        streams.queueBuffer.length ?? 1;
                    config.quantityOverride = aplDiscontinued ? 0 : null;
                    config.selectedVendorAttribute =
                        batchItemUpdate?.batchItemSnapshot
                            ?.selectedVendorAttribute ?? null;
                    return this.scanPipelineService.mapUpdatesToBatchItem(
                        batchItemUpdate,
                        config
                    );
                }),
                map((batchItemUpdate) =>
                    this.scanPipelineService.mapScannedOrderToBatchItem(
                        batchItemUpdate
                    )
                ),
                concatMap((batchItemUpdate) => {
                    const aplDiscontinued =
                        batchItemUpdate?.product?.aplDiscontinued;
                    const hasQuantity =
                        batchItemUpdate?.updatedBatchItem?.quantity > 0;
                    return aplDiscontinued || !hasQuantity
                        ? of(batchItemUpdate)
                        : this.scanPipelineService.uploadBatchItemChanges(
                              batchItemUpdate,
                              config
                          );
                }),
                finalize(() => {
                    queueBufferSubscription.unsubscribe();
                    queueBuffer.complete();
                    this.inProgressScans.delete(scanKey);
                })
            );
        } else {
            this.scanQueueService.queueScan(scanKey);
        }

        return apiRequest;
    }

    private getMatchingScanBuffer(scanKey: ScanKey): Observable<Array<ScanKey>> {
        return this.scanQueueService.bufferFlushes.pipe(
            map((queueBuffer) =>
                queueBuffer.filter((queuedScanKey) => queuedScanKey === scanKey)
            ),
            filter((queueBuffer) => queueBuffer.length > 0),
            take(1)
        );
    }

    /**
     * @description
     * Looks up the product data for a batch item and updates internal properties
     */
    rehydrateBatchItem(batchItem: BatchItem): Observable<BatchItemUpdate> {
        const config = new ProductScanConfiguration({
            searchText: batchItem.upc,
            selectedLegacyVendorId: batchItem.vendorId,
            quantityOverride: batchItem.quantity,
            productParameters: new ProductParameters({
                isExactPhrase: true,
                searchType: ProductSearchType.Upc,
            }),
            srpOverride: batchItem?.srp ?? null,
            wspOverride: batchItem?.wsp ?? null,
        });

        const initialBatchItemUpdate = new BatchItemUpdate({
            batchItemSnapshot: batchItem,
            product: null,
        });

        return this.userService.getCurrentUser().pipe(
            tap((currentUser) => {
                const isUpcSearch = !currentUser.settings.skuLookupEnabled;
                const vendorIds = [];
                if (!isUpcSearch && currentUser.settings.skuLookupVendorId) {
                    vendorIds.push(currentUser.settings.skuLookupVendorId);
                }

                config.user = currentUser;
                config.productParameters.searchType = isUpcSearch
                    ? ProductSearchType.Upc
                    : ProductSearchType.Sku;
                config.productParameters.vendors = vendorIds;
                // having no temporaryVendorId for a scan would lead to an empty result if we have no
                // selectedLegacyVendorId, so we default to the current temporaryVendorId in such a situation
                if (
                    !config.temporaryVendorId &&
                    !config.selectedLegacyVendorId
                ) {
                    config.temporaryVendorId =
                        currentUser.settings.currentTemporaryVendorId;
                }
            }),
            switchMap(() =>
                this.scanPipelineService.getBatchItemProductData(
                    initialBatchItemUpdate,
                    config
                )
            ),
            map((batchItemUpdate) =>
                this.scanPipelineService.mapUpdatesToBatchItem(
                    batchItemUpdate,
                    config
                )
            )
        );
    }
}
